// src/pages/Properties.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTIES, DELETE_PROPERTY, GET_DISTINCT_SUBURBS } from "../graphql/properties";
import { GET_ALL_AGENTS } from "../graphql/agents";
import { ListComponent } from "../components/ListComponent";
import { CardComponent } from "../components/CardComponent";
import { ListRowComponent } from "../components/ListRowComponent";
import { PropertyCardContentComponent } from "../components/PropertyCardContentComponent";
import { FilterBar } from "../components/FilterBar";
import { useNavigate } from 'react-router-dom';
import building from "../assets/images/property-gray.svg";
import { usePermissions } from '../shared/hooks/usePermissions';

const Properties = () => {
    const navigate = useNavigate();
    const permissions = usePermissions();

    const [searchTerm, setSearchTerm] = useState('');
    const [properties, setProperties] = useState({});
    const [sortBy, setSortBy] = useState('referenceNumber');
    const [viewMode, setViewMode] = useState('grid');
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Filter state
    const [activePill, setActivePill] = useState('all');
    const [selectedSuburbs, setSelectedSuburbs] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);

    // Distinct suburbs + agents for the dropdowns
    const suburbsQuery = useQuery(GET_DISTINCT_SUBURBS);
    const agentsQuery = useQuery(GET_ALL_AGENTS);

    const suburbOptions = useMemo(
        () => (suburbsQuery.data?.distinctSuburbs || []).map(s => ({ value: s, label: s })),
        [suburbsQuery.data]
    );
    const agentOptions = useMemo(
        () => (agentsQuery.data?.agents?.items || []).map(a => ({
            value: a.id,
            label: `${a.firstName} ${a.lastName}`.trim(),
        })),
        [agentsQuery.data]
    );

    // Build the GraphQL filter from search + pill + multi-selects
    const buildFilter = () => {
        const f = { isActive: true };

        // Search across multiple text fields
        if (searchTerm) {
            f.or = [
                { referenceNumber: { regex: searchTerm } },
                { "address.street": { regex: searchTerm } },
                { "address.suburb": { regex: searchTerm } },
                { "address.city": { regex: searchTerm } },
            ];
        }

        // Quick pills
        if (activePill === 'vacant') f.status = 'Vacant Rental';
        else if (activePill === 'occupied') f.status = 'Rented By Us';
        else if (activePill === 'pendingApproval') f.submissionStatus = 'PENDING';
        // 'needsAgent' translates to agent:exists:false — but only if no specific agents are selected
        if (activePill === 'needsAgent' && selectedAgents.length === 0) {
            f.agent = { exists: false };
        }

        // Suburb multi-select
        if (selectedSuburbs.length > 0) {
            f["address.suburb"] = { in: selectedSuburbs };
        }

        // Agent multi-select (overrides 'needsAgent' pill if both are set)
        if (selectedAgents.length > 0) {
            f.agent = { in: selectedAgents };
        }

        return f;
    };

    const query = useQuery(GET_PROPERTIES, {
        variables: {
            pageIndex,
            pageSize,
            orderAsc: true,
            orderField: "referenceNumber",
            filter: { isActive: true }
        },
        onCompleted: (data) => {
            setProperties(data?.properties);
        }
    });

    useEffect(() => {
        const fetchData = async () => {
            const { data } = await query.refetch({
                pageIndex,
                pageSize,
                orderAsc: true,
                orderField: sortBy,
                filter: buildFilter()
            });
            setProperties(data?.properties);
        };

        const debounceTimer = setTimeout(fetchData, 300);
        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, sortBy, pageIndex, pageSize, activePill, selectedSuburbs, selectedAgents]);

    const sortOptions = [
        { key: "referenceNumber", value: "Sort by Reference Number" },
        { key: "address.street", value: "Sort by Address" },
        { key: "address.suburb", value: "Sort by Suburb" },
        { key: "status", value: "Sort by Status" }
    ];

    const [deleteProperty] = useMutation(DELETE_PROPERTY, {
        refetchQueries: [{ query: GET_PROPERTIES, variables: { pageIndex: 0, pageSize: 10, orderAsc: true, orderField: "referenceNumber", filter: {} } }],
        onCompleted: () => console.log('Property deleted successfully'),
        onError: (error) => {
            console.error('Error deleting property:', error);
            alert('Failed to delete property. Please try again.');
        }
    });

    const handleDelete = (id, name) => {
        if (window.confirm(`Are you sure you want to delete property ${name}? This action cannot be undone.`)) {
            deleteProperty({ variables: { id } });
        }
    };

    const handleDetails = (id) => navigate(`/properties/${id}`);
    const handleEdit = (id) => navigate(`/properties/edit/${id}`);

    if (query.loading && !properties?.items) return (
        <div className="flex justify-center items-center h-64">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    if (query.error) return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {query.error.message}</span>
        </div>
    );

    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('referenceNumber');
        setActivePill('all');
        setSelectedSuburbs([]);
        setSelectedAgents([]);
        setPageIndex(0);
    };

    const handleViewModeChange = (mode) => setViewMode(mode);
    const handlePageSizeChange = (size) => { setPageSize(size); setPageIndex(0); };

    const handlePillChange = (key) => {
        setActivePill(key);
        setPageIndex(0);
    };
    const handleMultiSelectChange = (key, values) => {
        if (key === 'suburbs') setSelectedSuburbs(values);
        else if (key === 'agents') setSelectedAgents(values);
        setPageIndex(0);
    };

    const filterBar = (
        <FilterBar
            pills={[
                { key: 'all', label: 'All' },
                { key: 'vacant', label: 'Vacant' },
                { key: 'occupied', label: 'Occupied' },
                { key: 'needsAgent', label: 'Needs Agent' },
                { key: 'pendingApproval', label: 'Pending Approval' },
            ]}
            activePill={activePill}
            onPillChange={handlePillChange}
            multiSelects={[
                { key: 'suburbs', label: 'Suburb', options: suburbOptions, values: selectedSuburbs },
                { key: 'agents', label: 'Agent', options: agentOptions, values: selectedAgents },
            ]}
            onMultiSelectChange={handleMultiSelectChange}
            onClearAll={() => { setActivePill('all'); setSelectedSuburbs([]); setSelectedAgents([]); setPageIndex(0); }}
        />
    );

    return (
        <ListComponent
            title="Properties"
            headerIcon={building}
            addTitle="Property"
            addLink={permissions.canAssignPortfolio ? "/properties/add" : undefined}
            searchTerm={searchTerm}
            searchTermCallback={setSearchTerm}
            resetFiltersCallback={resetFilters}
            sortCallback={setSortBy}
            defaultSortOption={sortBy}
            sortOptions={sortOptions}
            viewMode={viewMode}
            viewModeCallback={handleViewModeChange}
            filterBar={filterBar}
            pageInfo={{
                totalCount: properties?.pageInfo?.totalCount,
                totalPages: properties?.pageInfo?.totalPages,
                pageSize: properties?.pageInfo?.pageSize,
                pageIndex: properties?.pageInfo?.pageIndex
            }}
            pageCallback={setPageIndex}
            pageSizeCallback={handlePageSizeChange}
        >
            {properties?.items?.map(property => {
                const componentProps = {
                    cardId: property.id,
                    deleteCallback: permissions.canAssignPortfolio ? () => handleDelete(property.id, property.referenceNumber || property.address?.street) : undefined,
                    editCallback: permissions.canAssignPortfolio ? handleEdit : undefined,
                    detailsCallback: handleDetails,
                    headerImageText: property.referenceNumber?.substring(0, 2) || 'PR',
                    headerTitle: property.referenceNumber || property.address?.street,
                    headerColor: 'bg-blue-100 text-blue-700',
                    children: <PropertyCardContentComponent
                        address={property.address}
                        status={property.status}
                        landlord={property.landlord}
                        tenant={property.tenant}
                        agent={property.agent}
                    />
                };

                return viewMode === 'list' ? (
                    <ListRowComponent key={property.id} {...componentProps} />
                ) : (
                    <CardComponent key={property.id} {...componentProps} />
                );
            })}
        </ListComponent>
    );
};

export default Properties;
